import { useState } from "react";

import {
  useStaffDetails,
  useStaffUser,
} from "../../../../hooks/queries/admin/staff";
import {
  ErrorMessage,
  LoadingOverlay,
  SectionCard,
  SectionHeader,
  SectionTitle,
  Tab,
  TabsContainer,
} from "../AdminPage.styles";
import { AuditLogPanel } from "../AuditLogPanel";
import { HistoryTabPanel, PanelStack } from "./StaffDetailsPanel.styles";
import { StaffRecordForm } from "./StaffRecordForm";
import { StaffUserForm } from "./StaffUserForm";

/** Audited tables behind the two history tabs (see the AuditLogPanel docs). */
const STAFF_TABLE = "hospital_portal_hospitalstaff";
const USER_TABLE = "cms_user";

type HistoryTab = "staff" | "user";

export interface IStaffDetailsPanelProps {
  staffId: number;
}

/**
 * Staff record + linked user account + audit history for one staff member —
 * the portal port of Retool's `moduleStaffDetails` (EN-5150).
 *
 * Shared on purpose: the Staff Details route renders it, and the Hospital
 * Details staff tab will too. **Embed it inline**, never inside the global
 * modal — the audit panels below open their own row drill-down there.
 *
 * Retool put the two audit trails behind "Staff history" / "User history"
 * links that opened modals; here they are tabs, which keeps the single modal
 * slot free for those drill-downs.
 */
export function StaffDetailsPanel({ staffId }: IStaffDetailsPanelProps) {
  const { staffDetails, isLoading, isError } = useStaffDetails(staffId);
  const { staffUser, isLoading: isUserLoading } = useStaffUser(
    staffDetails?.userId
  );

  const [historyTab, setHistoryTab] = useState<HistoryTab>("staff");

  if (isLoading) {
    return <LoadingOverlay>Loading staff details...</LoadingOverlay>;
  }

  if (isError || !staffDetails) {
    return (
      <ErrorMessage>
        Failed to load this staff member. Try refreshing the page.
      </ErrorMessage>
    );
  }

  return (
    <PanelStack>
      <StaffRecordForm staff={staffDetails} staffUser={staffUser} />

      {isUserLoading && (
        <LoadingOverlay>Loading the linked user account...</LoadingOverlay>
      )}
      {!isUserLoading && staffUser && <StaffUserForm user={staffUser} />}
      {!isUserLoading && !staffUser && (
        <ErrorMessage>
          Couldn&apos;t load the user account linked to this staff member.
        </ErrorMessage>
      )}

      <SectionCard>
        <SectionHeader>
          <SectionTitle>History</SectionTitle>
        </SectionHeader>

        <TabsContainer>
          <Tab
            type="button"
            active={historyTab === "staff"}
            onClick={() => setHistoryTab("staff")}
          >
            Staff history
          </Tab>
          <Tab
            type="button"
            active={historyTab === "user"}
            onClick={() => setHistoryTab("user")}
          >
            User history
          </Tab>
        </TabsContainer>

        <HistoryTabPanel>
          {historyTab === "staff" ? (
            <AuditLogPanel
              key="staff-history"
              tableName={STAFF_TABLE}
              entityId={staffDetails.id}
              title="Staff record changes"
            />
          ) : (
            <AuditLogPanel
              key="user-history"
              tableName={USER_TABLE}
              entityId={staffDetails.userId}
              title="User account changes"
            />
          )}
        </HistoryTabPanel>
      </SectionCard>
    </PanelStack>
  );
}
